import {customElement} from 'lit/decorators.js';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  first,
  identity,
  interval,
  map,
  NEVER,
  Observable,
  of,
  shareReplay,
  Subscribable,
  Subscription,
  takeUntil,
  tap,
} from 'rxjs';

import Decimal, {DecimalSource} from './break_infinity';
import {Shape} from './interfaces/shape';
import {p5, ri} from './runner-idle.js';
import {Gettable, LandmarkName, LandmarkSaveData} from './types/runner-idle.js';
import {UnlockableLitElement} from './unlockable-lit-element';
import {all$, any$} from './utils.js';
import {Vector} from './Vector.js';
import {Region} from './Region';
import {CompositeShape} from './CompositeShape';
import {call, compose, head, not, slice} from 'ramda';
import {Action} from './action';
import {persistObservable} from './decorators/persist.js';

export interface Positionable {
  // #region Properties (1)

  position: Vector;

  // #endregion Properties (1)
}

@customElement('ri-landmark')
export class Landmark extends UnlockableLitElement {
  // #region Properties (20)

  /**
   * Has the player been here before?
   * Do not visited$.next outside of in Landmark.setup!
   */
  @persistObservable(false, (v) =>
    any$([v]).pipe(distinctUntilChanged(), shareReplay(1))
  )
  public visited$!: BehaviorSubject<boolean>;

  public override baseTags = new Set(['landmark']);
  /** Tags that only apply in the central area of the landmark. */
  public centralTags: Set<string> = new Set();
  /** Is the player currently in this Landmark? */
  public containsPlayer$!: Observable<boolean>;
  public description = 'A landmark';
  public override displayName = 'Landmark';
  public interactions: (() => void)[] = [];
  public loot: Partial<Record<Gettable, DecimalSource>> = {};
  public override name: LandmarkName = 'landmark';
  public navigatable = true;
  /** Can the player navigate to this location? */
  public navigatable$: Observable<boolean> = of(true);
  /** The in-game shape of this Landmark. */
  public shape: Shape = new Vector();
  public speed: Decimal = new Decimal(1);
  // /** How fast are things here? Multiplicative modifier on movement. */
  // public speed: Decimal = new Decimal(1);
  public unlocks: UnlockableLitElement[] = [];
  public visibleOnMap = true;
  public visibleOnMap$ = new BehaviorSubject(true);
  public visited = false;
  /** z-index for drawing to the Map */
  public z: Decimal = new Decimal(0);

  public override get conditionString() {
    return `Reach ${this.displayName}`;
  }

  public override set conditionString(value) {
    this.baseConditionString = value;
  }

  // #endregion Properties (20)

  // #region Constructors (1)

  constructor() {
    super();
    // console.log('in constructor for', this);
    this.containsPlayer$ = ri.position$.pipe(
      map(this.containsPoint.bind(this))
    );
  }

  // #endregion Constructors (1)

  // #region Public Methods (9)

  public containsPoint(point: Vector) {
    return this.shape.contains(point);
  }

  public distance(point: Vector): Decimal {
    return this.shape.distance(point);
  }

  public draw(p: typeof p5, zoomScale: Decimal) {
    if (!this.visibleOnMap) return;
    let landmarkX = 0;
    let landmarkY = 0;
    let rotation = 0;
    if (this.shape instanceof Vector) {
      // Draw Vectors special, to indicate Landmark-ness. Consider subclassing ("Place extends Vector")
      landmarkX = this.shape.x.div(zoomScale).toNumber();
      landmarkY = this.shape.y.div(zoomScale).toNumber();
      const minLandmarkPixels = 10;
      const landmarkDiameter = new Decimal(1)
        .div(zoomScale)
        .max(minLandmarkPixels)
        .toNumber();
      p.push();
      p.strokeWeight(zoomScale.reciprocal().times(0.2).toNumber());
      // Selected landmark is red
      if (this === ri.nextLandmark) {
        p.fill(255, 191, 0);
        p.stroke(255, 191, 0);
      }
      // // Completed landmarks are green
      // if (landmark.unlocked) {
      //   p.fill(0, 255, 0);
      //   p.stroke(0, 255, 0);
      // }

      p.push();
      // Hollow out any selected
      if (this === ri.nextLandmark) {
        p.fill(0, 0, 0, 0);
      }
      p.ellipse(landmarkX, landmarkY, landmarkDiameter, landmarkDiameter);
      p.pop();

      p.pop();
    } else {
      if (this.shape instanceof Region) {
        landmarkX = this.shape.topLeft.x.div(zoomScale).toNumber();
        landmarkY = this.shape.topLeft.y.div(zoomScale).toNumber();
        rotation = this.shape.rotation;
      } else if (this.shape instanceof CompositeShape) {
        const shape = this.shape.shapes[0];
        if (shape instanceof Vector) {
          landmarkX = shape.x.div(zoomScale).toNumber();
          landmarkY = shape.y.div(zoomScale).toNumber();
        } else if (shape instanceof Region) {
          landmarkX = shape.topLeft.x.div(zoomScale).toNumber();
          landmarkY = shape.topLeft.y.div(zoomScale).toNumber();
          rotation = shape.rotation;
        }
      }
      this.shape.draw?.(p, zoomScale);
    }
    // Draw the label
    if (ri.settings.map.showLandmarkLabels) {
      p.push();
      p.rotate(rotation);
      p.translate(landmarkX + 6, landmarkY + 1);
      p.scale(1, -1);
      p.scale(ri.settings.map.landmarkLabelScale.value);
      p.rotate(-p.PI / 8);
      p.strokeWeight(1);
      p.stroke(0);
      p.text(this.displayName, 0, 0);
      p.pop();
    }
  }

  public hasTagsAt(point: Vector) {
    return this.shape.hasTagsAt(point);
  }

  public interactTick(_deltaTime: Decimal) {}

  public override loadFromJSON(data: LandmarkSaveData): void {
    super.loadFromJSON(data);
    this.visited$.next(data.visited);
  }

  public override setup(overrides: {shape: Shape} & Partial<Landmark>): void {
    // Landmark unlocks are defined in the game setup,
    // as this.position must already be set,
    // but should be defined before calling super.setup()
    // to allow for use in other setup functions.
    this.shape = overrides.shape;
    super.setup({
      unlock$: combineLatest([this.visited$, ri.position$]).pipe(
        filter(([visited]) => !visited),
        distinctUntilChanged(
          ([v1, p1], [v2, p2]) => v1 === v2 && p1.equals(p2)
        ),
        map(([visited, pos]) => visited || this.shape.contains(pos)),
        tap((val) => this.visited$.next(val)),
        distinctUntilChanged(),
        shareReplay(1)
      ),
      visible$: any$([this.navigatable$, this.unlock$]),
      ...overrides,
    });
    this.unlock$.pipe(filter(identity)).subscribe(() => {
      ri.log(`<strong class="danger">${this.description}</strong>`, [
        'persistent',
        'landmark',
      ]);
      setTimeout(ri.updateNextLandmark);
      ri.requestUpdate();
    });
    // Upon interact (Enter/Step onto Landmark)
    let looted = false;
    this.containsPlayer$.pipe(filter(Boolean)).subscribe(() => {
      if (looted) return;
      // Gain loot upon interact
      Object.entries(this.loot).forEach(([k, v]) => ri.gain(k as Gettable, v));
      // Interact
      this.interactions.forEach((interaction) => interaction.call(this));
      // TODO Couldn't figure out how to use the stream correctly
      if (this.temporaryFinished$.value) {
        looted = true;
      }
    });
    // Each tick while on the Landmark
    combineLatest([ri.tick$, ri.position$, this.containsPlayer$])
      .pipe(
        distinctUntilKeyChanged(0),
        filter(([, , containsPlayer]) => Boolean(containsPlayer)),
        filter(([, pos]) => !ri.pointHasTag(pos, 'blockInteractTick')),
        map(head)
      )
      .subscribe(this.interactTick.bind(this));
    this.subscribe('visited', this.visited$);
    this.subscribe('visibleOnMap', this.visibleOnMap$);
    this.subscribe('navigatable', this.navigatable$);
  }

  public override toJSON(): {} {
    return {...super.toJSON(), visited: this.visited};
  }

  public override toString(): string {
    return `Landmark: ${this.name} ${this.shape}`;
  }

  // #endregion Public Methods (9)
}
